import { useInvalidate, useNotification } from '@refinedev/core'
import type { Cabinet } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { RECORD_ACTIVITY_RESOURCE } from '~/providers/dataProvider'
import { errorMessage } from '~/providers/errors'
import { statusActionFor, statusLabel } from './cabinetStatus'

const STATUS_BADGE = {
  active: 'default',
  disabled: 'secondary',
  decommissioned: 'outline',
} as const satisfies Record<Cabinet['status'], string>

export function CabinetStatusBadge({ status }: { status: Cabinet['status'] }) {
  const { t } = useTranslation()
  return <Badge variant={STATUS_BADGE[status]}>{statusLabel(status, t)}</Badge>
}

/** Disable / Enable behind a confirmation, via `cabinets.setStatus`. */
export function CabinetStatusAction({ cabinet }: { cabinet: Cabinet }) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const invalidate = useInvalidate()
  const { open } = useNotification()
  const action = statusActionFor(cabinet.status, t)
  const { mutate, isPending } = useMutation({
    mutationFn: (status: 'active' | 'disabled') =>
      client.cabinets.setStatus({ id: cabinet.id, status }),
    onSuccess: async (_cabinet, status) => {
      await Promise.all([
        invalidate({
          resource: 'cabinets',
          invalidates: ['list', 'detail'],
          id: cabinet.id,
        }),
        // the show page's History lists the new event
        invalidate({
          resource: RECORD_ACTIVITY_RESOURCE,
          invalidates: ['list'],
        }),
      ])
      open?.({
        type: 'success',
        message: t(
          status === 'disabled'
            ? 'cabinetsPage.status.disabledToast'
            : 'cabinetsPage.status.enabledToast',
          { name: cabinet.name },
        ),
      })
    },
    onError: (error) => {
      open?.({
        type: 'error',
        message: t('cabinetsPage.status.failedTitle'),
        description: errorMessage(error),
      })
    },
  })

  if (action === null) {
    return null
  }

  return (
    <ConfirmDialog
      trigger={
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
        >
          {action.label}
        </Button>
      }
      title={t(
        action.next === 'disabled'
          ? 'cabinetsPage.status.disableTitle'
          : 'cabinetsPage.status.enableTitle',
        { name: cabinet.name },
      )}
      description={
        action.next === 'disabled'
          ? t('cabinetsPage.status.disableDescription')
          : t('cabinetsPage.status.enableDescription')
      }
      confirmLabel={action.label}
      destructive={action.next === 'disabled'}
      onConfirm={() => mutate(action.next)}
    />
  )
}
